import React, { useMemo } from "react";

import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button
} from "@heroui/react";

export const WarningIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6"
  >
    <mask
      id="mask0_2204_10"
      style={{ maskType: "alpha" }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect width="24" height="24" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_2204_10)">
      <path
        d="M12 17C12.2833 17 12.5208 16.9042 12.7125 16.7125C12.9042 16.5208 13 16.2833 13 16C13 15.7167 12.9042 15.4792 12.7125 15.2875C12.5208 15.0958 12.2833 15 12 15C11.7167 15 11.4792 15.0958 11.2875 15.2875C11.0958 15.4792 11 15.7167 11 16C11 16.2833 11.0958 16.5208 11.2875 16.7125C11.4792 16.9042 11.7167 17 12 17ZM11 13H13V7H11V13ZM8.25 21L3 15.75V8.25L8.25 3H15.75L21 8.25V15.75L15.75 21H8.25Z"
        fill="#573800"
      />
    </g>
  </svg>
);
export const SuccessIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.6 14.6L15.65 7.55L14.25 6.15L8.6 11.8L5.75 8.95L4.35 10.35L8.6 14.6ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20Z"
      fill="#06371D"
    />
  </svg>
);

const TokenSwapTable = ({
  tableColumns = [],
  tableData = [],
  emptyDataContent
}) => {
  const renderCell = React.useCallback((data, columnKey) => {
    const cellValue = data[columnKey];

    switch (columnKey) {
      case "id":
        return (
          <div className="flex flex-col">
            <p className="font-sans text-lg text-block font-semibold">
              {cellValue}
            </p>
          </div>
        );
      case "created_at":
        return (
          <div className="flex flex-col">
            <p className="font-sans text-lg text-block font-normal">
              {cellValue ? new Date(cellValue).toLocaleDateString() : "N/A"}
            </p>
          </div>
        );
      case "exchange_rate":
        // Use swap_rate if available, otherwise show exchange_rate
        const exchangeRate = data.swap_rate || data.exchange_rate;
        return (
          <div className="flex flex-col">
            <span className="font-sans text-lg text-block font-normal">
              {exchangeRate ? `${exchangeRate}` : "N/A"}
            </span>
          </div>
        );
      case "swap":
        // Concatenate from and to token information
        const fromInfo =
          data.from_amount && data.from_symbol
            ? `${data.from_amount} ${data.from_symbol}`
            : "N/A";
        const toInfo =
          data.to_amount && data.to_symbol
            ? `${data.to_amount} ${data.to_symbol}`
            : "N/A";
        const swapDisplay =
          fromInfo && toInfo && fromInfo !== "N/A" && toInfo !== "N/A"
            ? `${fromInfo} → ${toInfo}`
            : fromInfo || toInfo || "N/A";

        return (
          <div className="flex flex-col">
            <span className="font-sans text-lg text-block font-normal">
              {swapDisplay}
            </span>
          </div>
        );
      case "status":
        return (
          <div className="relative flex justify-end items-center gap-2">
            <Button
              className={`${
                cellValue === "Failed" ? "bg-[#F5D113]" : "bg-[#1FEF68]"
              } border ${
                cellValue === "Failed" ? "border-[#F5D113]" : "border-[#1FEF68]"
              } text-base font-bold ${
                cellValue === "Failed" ? "text-[#573800]" : "text-[#573800]"
              } h-10 min-w-28`}
              radius="full"
              startContent={
                cellValue === "Failed" ? <WarningIcon /> : <SuccessIcon />
              }
            >
              {cellValue ? cellValue : "N/A"}
            </Button>
          </div>
        );
      default:
        return cellValue;
      case "transaction_hash":
        if (!cellValue) return "N/A";

        const explorerUrl = `https://testnet.bscscan.com/tx/${cellValue}`;

        return (
          <a
            href={explorerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline font-medium"
          >
            Click To View Tx
          </a>
        );
    }
  }, []);

  const classNames = useMemo(
    () => ({
      base: ["p-0"],
      th: [
        "bg-transparent",
        "text-black",
        "border-b",
        "border-[#C2C2C2]",
        "py-4",
        "px-8",
        "whitespace-nowrap",
        "text-lg",
        "font-semibold"
      ],
      td: [
        "py-3",
        "px-8",
        // changing the rows border radius
        // first
        "group-data-[first=true]/tr:first:before:rounded-none",
        "group-data-[first=true]/tr:last:before:rounded-none",
        // middle
        "group-data-[middle=true]/tr:before:rounded-none",
        // last
        "group-data-[last=true]/tr:first:before:rounded-none",
        "group-data-[last=true]/tr:last:before:rounded-none"
      ]
    }),
    []
  );

  return (
    <div className="z-0 flex flex-col relative justify-between gap-4 bg-content1 overflow-auto rounded-large w-full border border-[#C2C2C2]">
      <Table
        aria-label="Example table with dynamic content"
        removeWrapper
        classNames={classNames}
      >
        <TableHeader columns={tableColumns}>
          {(column) => (
            <TableColumn
              key={column.uid}
              align={column.uid === "status" ? "end" : "start"}
            >
              {column.label}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody emptyContent={emptyDataContent} items={tableData}>
          {(item) => (
            <TableRow key={item?.id}>
              {(columnKey) => (
                <TableCell>{renderCell(item, columnKey)}</TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default TokenSwapTable;
